/**
 * Data Import Page
 *
 * Admin page for initial data setup and import operations.
 */

import { useState } from 'react';
import { Layout } from '../../components/Layout';
import {
  useDataSummary,
  useSeedAll,
  useSeedNiveaus,
  useSyncEmployees,
} from './useDataImport';
import type { ImportResult, SeedAllResults } from '../../lib/dataImportApi';

export function DataImportPage() {
  const { data: summary, isLoading, error, refetch } = useDataSummary();
  const seedAll = useSeedAll();
  const seedNiveaus = useSeedNiveaus();
  const syncEmployees = useSyncEmployees();

  const [lastResult, setLastResult] = useState<ImportResult | SeedAllResults | null>(null);

  async function handleSeedAll() {
    try {
      const result = await seedAll.mutateAsync();
      setLastResult(result);
    } catch {
      // Error handled by mutation state
    }
  }

  async function handleSeedNiveaus() {
    try {
      const result = await seedNiveaus.mutateAsync();
      setLastResult(result);
    } catch {
      // Error handled by mutation state
    }
  }

  async function handleSyncEmployees() {
    try {
      const result = await syncEmployees.mutateAsync();
      setLastResult(result);
    } catch {
      // Error handled by mutation state
    }
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Data laden...</span>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700">Fout bij het laden van data.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="data-import-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Data Import & Setup</h1>
          <p className="mt-1 text-sm text-gray-500">
            Initiële data-import en systeem configuratie (alleen beheerders)
          </p>
        </div>

        {/* Current Data Summary */}
        <div className="mb-6 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Huidige Data</h2>
            <button
              onClick={() => refetch()}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Vernieuwen
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{summary?.niveaus ?? 0}</div>
              <div className="text-sm text-gray-500">Niveaus</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{summary?.employees ?? 0}</div>
              <div className="text-sm text-gray-500">Medewerkers</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{summary?.tasks ?? 0}</div>
              <div className="text-sm text-gray-500">Taken</div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{summary?.competencies.total ?? 0}</div>
              <div className="text-sm text-gray-500">Competenties</div>
              <div className="text-xs text-gray-400">
                ({summary?.competencies.basis ?? 0} basis, {summary?.competencies.taak ?? 0} taak)
              </div>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold text-gray-900">{summary?.taskAssignments ?? 0}</div>
              <div className="text-sm text-gray-500">Toewijzingen</div>
            </div>
          </div>
        </div>

        {/* Import Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Seed All Data */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-start">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-gray-900">Alle Standaarddata</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Laad alle standaarddata: 6 niveaus, voorbeeldtaken en competenties.
                </p>
                <button
                  onClick={handleSeedAll}
                  disabled={seedAll.isPending}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {seedAll.isPending ? 'Bezig...' : 'Alle Data Laden'}
                </button>
              </div>
            </div>
          </div>

          {/* Seed Niveaus Only */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-start">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-gray-900">Alleen Niveaus</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Laad alleen de 6 standaard functieniveaus met criteria.
                </p>
                <button
                  onClick={handleSeedNiveaus}
                  disabled={seedNiveaus.isPending}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {seedNiveaus.isPending ? 'Bezig...' : 'Niveaus Laden'}
                </button>
              </div>
            </div>
          </div>

          {/* Sync Employees */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-start">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                <svg className="h-5 w-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-medium text-gray-900">Medewerkers Synchroniseren</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Synchroniseer medewerkers vanuit de gebruikersdatabase.
                </p>
                <button
                  onClick={handleSyncEmployees}
                  disabled={syncEmployees.isPending}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {syncEmployees.isPending ? 'Bezig...' : 'Synchroniseren'}
                </button>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Informatie</h3>
                <p className="mt-1 text-sm text-yellow-700">
                  Data import is bedoeld voor initiële setup. Bestaande data wordt bijgewerkt, niet verwijderd.
                  Gebruik voor productieomgevingen de specifieke beheerpagina's.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Last Result */}
        {lastResult && (
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Laatste Resultaat</h3>
            {'results' in lastResult ? (
              // SeedAllResults
              <div className="space-y-4">
                <p className="text-sm text-gray-600">{lastResult.message}</p>
                {(['niveaus', 'tasks', 'competencies'] as const).map((key) => (
                  <div key={key} className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium capitalize">{key}</span>
                      <span className={lastResult.results[key].success ? 'text-green-600' : 'text-yellow-600'}>
                        {lastResult.results[key].success ? 'Succes' : 'Gedeeltelijk'}
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                      Aangemaakt: {lastResult.results[key].created} | Bijgewerkt: {lastResult.results[key].updated}
                      {lastResult.results[key].errors.length > 0 && (
                        <div className="mt-2 text-red-600">
                          Fouten: {lastResult.results[key].errors.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // ImportResult
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Resultaat</span>
                  <span className={lastResult.success ? 'text-green-600' : 'text-yellow-600'}>
                    {lastResult.success ? 'Succes' : 'Gedeeltelijk'}
                  </span>
                </div>
                <div className="mt-2 text-sm text-gray-500">
                  Aangemaakt: {lastResult.created} | Bijgewerkt: {lastResult.updated}
                  {lastResult.errors.length > 0 && (
                    <div className="mt-2 text-red-600">
                      Fouten: {lastResult.errors.join(', ')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
